import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import Loader from "../dist/Loader";
import CustomButton from "./CustomButton";

const EmployeeDeviceCount = () => {
  const [states, setStates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [brandCounts, setBrandCounts] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [showTable, setShowTable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetch(`${BASE_URL}/api/states`, { headers: getAuthHeaders() })
      .then((res) => res.json())
      .then((data) => setStates(data));
  }, []);

  const handleGoClick = async () => {
    if (!selectedState) return;

    const stateObj = states.find((s) => s.name === selectedState);
    if (!stateObj) return;

    setIsLoading(true);

    try {
      const [empRes, brandRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/employees?state_id=${stateObj.id}`, { headers: getAuthHeaders() }),
        axios.get(`${BASE_URL}/api/devices/brand-summary?state_id=${stateObj.id}`, { headers: getAuthHeaders() }),
      ]);

      setEmployees(empRes.data);
      setBrandCounts(brandRes.data);
      setShowTable(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getEmployeeRows = () => {
    let index = 1;
    return employees.flatMap((emp) =>
      emp.devices?.length > 0
        ? emp.devices.map((device) => ({
            ...emp,
            deviceData: device,
            rowNumber: index++,
          }))
        // : [{ ...emp, deviceData: {}, rowNumber: index++ }]
        : []
    );
  };

  return (
    <div className="min-h-screen w-full p-2 flex flex-col">
      {isLoading && <Loader />}

      <div className={`${isLoading ? "opacity-40 pointer-events-none" : ""}`}>

        {/* Header */}
        <div className="bg-white border rounded-lg p-2 shadow mt-6">
          <h1 className="text-center font-bold text-2xl text-gray-800">
            <span className="text-blue-700">Device </span>
            <span className="text-blue-500">History</span>
          </h1>

          {/* Filter */}
          <div className="flex flex-wrap justify-center gap-3 mt-2">
            <label className="font-semibold text-gray-700 text-lg">State</label>

            <select
              className="border rounded px-3 py-2 text-sm"
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
            >
              <option value="">Select State</option>
              {states.map((st) => (
                <option key={st.id} value={st.name}>
                  {st.name}
                </option>
              ))}
            </select>

            <button
              onClick={handleGoClick}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              GO
            </button>
          </div>
        </div>

        {/* Employee Table */}
        {showTable && (
          <>
           <div className="border rounded shadow  table-wrapper mt-5">
        <table className="unlock-dcr-leave-table text-sm ">
          <thead className="bg-blue-500 text-white">
                  <tr>
                    {[
                      "S.No","Emp ID","Employee Name","Designation","HQ","Manager","State","Division","Phone",
                      "Brand","Reg. ID","Device ID","Model","Version","Reinstalls","Install Date"
                    ].map((header) => (
                      <th key={header} className="p-2 border">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {getEmployeeRows().map((row, i) => (
                    <tr key={i} className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}>
                      <td className="border p-1 text-center">{row.rowNumber}</td>
                      <td className="border p-1">{row.employee}</td>
                      <td className="border p-1">{row.first_name} {row.last_name}</td>
                      <td className="border p-1">{row.designation}</td>
                      <td className="border p-1">{row.headquarter_name}</td>
                      <td className="border p-1">{row.manager}</td>
                      <td className="border p-1">{row.state_name}</td>
                      <td className="border p-1">{row.division}</td>
                      <td className="border p-1">{row.mobile}</td>
                      <td className="border p-1">{row.deviceData.registered_brand || "-"}</td>
                      <td className="border p-1">{row.deviceData.registered_device_id || "-"}</td>
                      <td className="border p-1">{row.deviceData.device_id || "-"}</td>
                      <td className="border p-1">{row.deviceData.model || "-"}</td>
                      <td className="border p-1">{row.deviceData.version || "-"}</td>
                      <td className="border p-1">{row.deviceData.reinstalls || 0}</td>
                      <td className="border p-1">
                        {row.deviceData.latest_install_date
                          ? new Date(row.deviceData.latest_install_date).toLocaleString()
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Brand Table */}
            <div className="mt-6 p-4 bg-white rounded-lg shadow">
              <h1 className="text-center text-2xl font-bold text-gray-800 mb-3">
                <span className="text-blue-700">Brand Device </span>
                <span className="text-blue-500">Count</span>
              </h1>

              <div className="overflow-auto max-h-[70vh] border rounded-lg shadow">
                <table className="min-w-full text-sm">
                  <thead className="bg-blue-600 text-white">
                    <tr>
                      <th className="border p-2">S.No</th>
                      <th className="border p-2">Brand</th>
                      <th className="border p-2">Device Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {brandCounts.map((b, i) => (
                      <tr key={i} className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}>
                        <td className="border p-2 text-center">{i + 1}</td>
                        <td className="border p-2">{b.brand}</td>
                        <td className="border p-2">{b.device_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeDeviceCount;
